import { Shape, Vector3 } from 'three';
import { generateUUID } from 'three/src/math/MathUtils';
import {
  buildActiveMap,
  createExtrudeSettings,
  createRectangleShape,
  getLengthWithKey,
  getWidthMonument,
  SIDES_INFORMATIONS,
} from './common.utils';

export const getPositionWithIndex = (
  key: 'front' | 'back' | 'left' | 'right',
  depth: { front: number; back: number; left: number; right: number },
  pieces: any[],
  customSoles: any[] = [],
  customBoxes: any[] = [],
  customPlatingsIsActive: { [key: string]: boolean },
  length: number,
  width: number,
  mode: 'closoir' | 'double closoir' | 'tete',
) => {
  const box = getWidthMonument({
    pieces,
    customElements: [...customSoles, ...customBoxes],
  });

  const deltaX = box.backRight.x - box.backLeft.x - width;
  const deltaZ = mode === 'closoir' ? depth.back / 2 : 0;

  if (box) {
    if (key === 'front') {
      let x = box.frontLeft.x + width / 2 + deltaX / 2;
      let y = box.frontLeft.y;
      let z = box.backLeft.z + length - depth.front;
      return new Vector3(x, y, z);
    }
    if (key === 'back') {
      let x = box.backLeft.x + width / 2 + deltaX / 2;
      let y = box.backLeft.y;
      let z = box.backLeft.z;

      return new Vector3(x, y, z);
    }
    if (key === 'left') {
      let x = box.backLeft.x + deltaX / 2;
      let y = box.frontLeft.y;
      let z = box.backLeft.z + length / 2 - deltaZ;

      return new Vector3(x, y, z);
    }
    if (key === 'right') {
      let x = box.backLeft.x + width - depth.right + deltaX / 2;
      let y = box.frontRight.y;
      let z = box.backRight.z + length / 2 - deltaZ;

      return new Vector3(x, y, z);
    }
  }
};

export const createPlatings = (
  pieces: any[],
  customSoles: any[] = [],
  customPlatings: any[] = [],
  customBoxes: any[] = [],
  setCustomPlatings: (customPlatings: any[]) => void,
  customSize?: {
    length?: number;
    width?: number;
    heightFront?: number;
    heightBack?: number;
    orientation?: 'x' | 'z';
    hasSlope?: boolean;
    mode?: 'closoir' | 'double closoir' | 'tete';
    depth?: number;
  },
) => {
  const orientation = customSize?.orientation ?? 'z';
  const mode = customSize?.mode ?? 'closoir';
  const depth = customSize?.depth ?? 0.03;

  let platings: any[] = [];

  const monumentBox = getWidthMonument({
    pieces,
    customElements: [...customSoles, ...customBoxes],
  });
  let monumentWidth = monumentBox.frontRight.x - monumentBox.frontLeft.x;
  let monumentLength = monumentBox.frontRight.z - monumentBox.backRight.z;

  if (customSize) {
    if (customSize.length) monumentLength = customSize.length;
    if (customSize.width) monumentWidth = customSize.width;
  }

  if (monumentLength === 0) monumentLength = 2.4;
  if (monumentWidth === 0) monumentWidth = 1.4;

  const customPlatingsIsActive = buildActiveMap(customPlatings);

  SIDES_INFORMATIONS.forEach((el, elIndex) => {
    const shape = new Shape();
    const length = getLengthWithKey(
      mode,
      el.key as 'front' | 'back' | 'left' | 'right',
      {
        front: depth,
        back: depth,
        left: depth,
        right: depth,
      },
      el.key === 'front' || el.key === 'back' ? monumentWidth : monumentLength,
    );

    let shapeDetails = {
      display: true,
      isRotated: el.isRotated,
      depth: depth,
      length: length,
      front: customSize?.heightFront,
      back: customSize?.heightBack,
      monumentWidth,
      monumentLength,
      monumentFront: customSize?.heightFront ?? 0.2,
      monumentBack: customSize?.heightBack,
      hasSlope: customSize?.hasSlope,
      orientation: orientation,
      mode,
    };

    if (customSize) {
      if (orientation === 'x' && customSize.heightBack !== undefined) {
        if (el.key === 'left') {
          shapeDetails.back = customSize.heightFront ?? 0.2;
        }
        if (el.key === 'right') {
          shapeDetails.front =
            customSize.heightBack === undefined ? 0.2 : customSize.heightBack;
        }
      }
      if (orientation === 'z' && customSize.heightBack !== undefined) {
        if (el.key === 'front') {
          shapeDetails.back = customSize.heightFront ?? 0.2;
        }
        if (el.key === 'back') {
          shapeDetails.front =
            customSize.heightBack === undefined ? 0.2 : customSize.heightBack;
        }
      }
    }

    if (shapeDetails.front === undefined) shapeDetails.front = 0.2;

    createRectangleShape(shape, length, shapeDetails.front, shapeDetails.back);

    platings.push({
      texture: customPlatings[elIndex]?.texture,
      granit: customPlatings[elIndex]?.granit,
      id: generateUUID(),
      key: el.key,
      shape,
      shapeDetails,
      isActive: customPlatings[elIndex]
        ? customPlatings[elIndex].isActive
        : true,
      position: getPositionWithIndex(
        el.key as 'front' | 'back' | 'left' | 'right',
        {
          front: shapeDetails.depth,
          back: shapeDetails.depth,
          left: shapeDetails.depth,
          right: shapeDetails.depth,
        },
        pieces,
        customSoles,
        customBoxes,
        customPlatingsIsActive,
        monumentLength,
        monumentWidth,
        mode,
      ),
      extrudeSettings: createExtrudeSettings(depth),
    });
  });

  setCustomPlatings(platings);
};
